/**
 * Mega Menu (desktop) + Mobile Nav Drawer focus trap.
 * Vanilla ES6+, no external dependencies.
 *
 * The mobile drawer's own drill-down submenu behavior is handled by the
 * theme's existing mmenu-light integration (see main.build.min.js), which
 * scans #menu's nested <ul> markup at load time — this file only adds a
 * focus trap on top of it and does not touch its navigation/open/close
 * behavior.
 */
(function () {
  'use strict';

  var HOVER_OPEN_DELAY = 150;
  var HOVER_CLOSE_DELAY = 200;
  var DESKTOP_MIN_WIDTH = 992;

  /* --------------------------------------------------------------------
   * Desktop mega menu: hover intent + keyboard support
   * ------------------------------------------------------------------ */
  function initMegaMenu() {
    var items = document.querySelectorAll('.js-mega-menu-item');
    if (!items.length) return;

    var openItem = null;

    function isDesktop() {
      return window.matchMedia('(min-width: ' + DESKTOP_MIN_WIDTH + 'px)').matches;
    }

    function setExpanded(item, expanded) {
      var trigger = item.querySelector('.js-mega-menu-trigger');
      var panel = item.querySelector('.js-mega-menu-panel');
      if (!trigger || !panel) return;
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      panel.classList.toggle('is-open', expanded);
      panel.toggleAttribute('inert', !expanded);
      item.classList.toggle('js-mega-menu-item--open', expanded);
    }

    function openMenu(item) {
      if (!isDesktop()) return;
      if (openItem && openItem !== item) {
        closeMenu(openItem, { immediate: true });
      }
      clearTimers(item);
      setExpanded(item, true);
      openItem = item;
    }

    function closeMenu(item, options) {
      clearTimers(item);
      setExpanded(item, false);
      if (openItem === item) openItem = null;
    }

    function clearTimers(item) {
      if (item._megaOpenTimer) {
        window.clearTimeout(item._megaOpenTimer);
        item._megaOpenTimer = null;
      }
      if (item._megaCloseTimer) {
        window.clearTimeout(item._megaCloseTimer);
        item._megaCloseTimer = null;
      }
    }

    items.forEach(function (item) {
      var trigger = item.querySelector('.js-mega-menu-trigger');

      item.addEventListener('mouseenter', function () {
        if (!isDesktop()) return;
        if (item._megaCloseTimer) {
          window.clearTimeout(item._megaCloseTimer);
          item._megaCloseTimer = null;
        }
        item._megaOpenTimer = window.setTimeout(function () {
          openMenu(item);
        }, HOVER_OPEN_DELAY);
      });

      item.addEventListener('mouseleave', function () {
        if (item._megaOpenTimer) {
          window.clearTimeout(item._megaOpenTimer);
          item._megaOpenTimer = null;
        }
        item._megaCloseTimer = window.setTimeout(function () {
          closeMenu(item);
        }, HOVER_CLOSE_DELAY);
      });

      item.addEventListener('focusin', function () {
        if (!isDesktop()) return;
        openMenu(item);
      });

      item.addEventListener('focusout', function (e) {
        if (!isDesktop()) return;
        var next = e.relatedTarget;
        if (next && item.contains(next)) return;
        closeMenu(item);
      });

      if (trigger) {
        trigger.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' || e.key === 'Esc') {
            if (item.classList.contains('js-mega-menu-item--open')) {
              closeMenu(item);
              trigger.focus();
            }
          }
        });
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (!openItem) return;
      var trigger = openItem.querySelector('.js-mega-menu-trigger');
      closeMenu(openItem);
      if (trigger) trigger.focus();
    });

    window.addEventListener('resize', function () {
      if (!isDesktop() && openItem) {
        closeMenu(openItem);
      }
    });
  }

  /* --------------------------------------------------------------------
   * Mobile off-canvas drawer: focus trap while open.
   * Hooks into the theme's existing open/close triggers without altering
   * their own show/hide behavior.
   * ------------------------------------------------------------------ */
  function initMobileDrawerFocusTrap() {
    var drawer = document.getElementById('menu');
    if (!drawer) return;

    var openTriggers = document.querySelectorAll('.js-mobile-menu-toggle');
    var closeTrigger = document.getElementById('mmmenu-close-button');
    var overlay = document.querySelector('.js-menu-overlay');

    var isTrapped = false;
    var lastFocused = null;

    function focusableElements() {
      return Array.prototype.slice
        .call(
          drawer.querySelectorAll(
            'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        )
        .filter(function (el) {
          return el.offsetParent !== null;
        });
    }

    function trapFocus() {
      isTrapped = true;
      lastFocused = document.activeElement;
      var focusable = focusableElements();
      if (focusable.length) {
        window.setTimeout(function () {
          focusable[0].focus();
        }, 50);
      }
    }

    function releaseFocus() {
      isTrapped = false;
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
      lastFocused = null;
    }

    openTriggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        trapFocus();
      });
    });

    if (closeTrigger) {
      closeTrigger.addEventListener('click', function () {
        releaseFocus();
      });
    }

    if (overlay) {
      overlay.addEventListener('click', function () {
        if (isTrapped) releaseFocus();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (!isTrapped) return;

      if (e.key === 'Escape' || e.key === 'Esc') {
        if (closeTrigger) closeTrigger.click();
        releaseFocus();
        return;
      }

      if (e.key !== 'Tab') return;

      var focusable = focusableElements();
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  function init() {
    initMegaMenu();
    initMobileDrawerFocusTrap();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
